import axios, { AxiosResponse } from 'axios'
import {
  QualificationApplicationResponse,
  Resources, ContractType,
} from '@hankintailmoitukset.fi/hilma-espd-edm'
import ValidationState from '@/enums/validationState'
import { EspdRequest } from './espd-requests'

export interface EspdResponse {
  id: number;
  uuid: string;
  projectId: number;
  noticeId: number;
  noticeType: string;
  contractType: ContractType;
  lots: string[];
  qualificationApplicationResponse: QualificationApplicationResponse;
  language: string;
  tenderWorkspaceLink: string;
  tenderId: string;
  title: string;
  validationState: ValidationState;
  requestId: number;
  request: EspdRequest;
}

// Fetch list of all criteria
export function getEspdCriteria() {
  return Resources.criterionSpecification
}

export async function createEspdResponse(host: string, payload: EspdResponse) {
  const url = `${host}api/responses`
  const result = await axios.post<EspdResponse, AxiosResponse<EspdResponse>>(url, payload)
  return result.data
}

export async function saveEspdResponse(identifier: number, host: string, payload: EspdResponse) {
  const url = `${host}api/responses/${identifier}`
  await axios.put<EspdResponse, AxiosResponse<EspdResponse>>(url, payload)
}

// Save the espd request using access token instead of bearer token
export async function saveEspdResponseAnonymous(
  identifier: number,
  host: string,
  payload: EspdResponse,
  accessToken: string,
) {
  const url = `${host}api/anonymousresponses/${identifier}?accessToken=${accessToken}`
  await axios.put(
    url,
    payload,
    {
      flags: {
        disableBearerAuthentication: true,
      },
    },
  )
}

// Fetch the ESPD response from the server by id
export async function getEspdResponse(identifier: number, host: string) {
  const url = `${host}api/responses/${identifier}`
  try {
    const returned = await axios.get<string, AxiosResponse<EspdResponse>>(url)
    return returned.data
  } catch (error) {
    return undefined
  }
}

// Fetch the ESPD request using access token instead of bearer token
export async function getEspdResponseAnonymous(
  identifier: number,
  host: string,
  accessToken: string,
) {
  const url = `${host}api/anonymousresponses/${identifier}?accessToken=${accessToken}`
  try {
    const returned = await axios.get(
      url,
      {
        flags: {
          disableBearerAuthentication: true,
        },
      },
    )
    return returned.data as EspdResponse
  } catch (error) {
    return undefined
  }
}

export async function getResponseXmlAnonymous(
  id: string,
  host: string,
  accessToken: string,
): Promise<string | undefined> {
  const url = `${host}api/anonymousresponses/${id}/xml?accessToken=${accessToken}`
  try {
    const returned = await axios.get(url)
    return returned.data as string
  } catch (error) {
    return undefined
  }
}

// Fetch the ESPD response from the server by id
export async function getEspdResponseForReview(identifier: number, host: string) {
  const url = `${host}api/responses/review/${identifier}`
  try {
    const returned = await axios.get<string, AxiosResponse<EspdResponse>>(url)
    return returned.data
  } catch (error) {
    return undefined
  }
}

// Fetch the ESPD response from server
export async function getMyEspdResponseByRequestId(uuid: number, host: string) {
  const url = `${host}api/responses/requests/${uuid}/mine`
  try {
    const returned = await axios.get<string, AxiosResponse<EspdResponse>>(url)
    return returned.data
  } catch (error) {
    return undefined
  }
}

type Dictionary < T > = { [key: string]: T }
export function getAccessTokenQueryParameter(
  query: Dictionary<string | (string | null)[]>,
) {
  const keys = Object.keys(query)
  for (let i = 0; i < keys.length; i++) {
    if (keys[i].toLocaleLowerCase() === 'accesstoken') {
      return query[keys[i]]
    }
  }

  return undefined
}

export async function importXMLResponse(identifier: number, host: string, file: File) {
  const url = `${host}api/responses/${identifier}/xmlimport`

  const formData = new FormData()
  formData.append('file', file)

  const returned = await axios.put<FormData, AxiosResponse<EspdResponse>>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return returned.data
}

export async function importXMLResponseAnonymous(
  identifier: number,
  host: string,
  file: File,
  accessToken: string,
) {
  const url = `${host}api/anonymousresponses/${identifier}/xmlimport?accessToken=${accessToken}`

  const formData = new FormData()
  formData.append('file', file)

  const returned = await axios.put<FormData, AxiosResponse<EspdResponse>>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return returned.data
}
